/**
 * The WebSocket that brings the user's background jobs (OSA-102). The server sends typed messages: {type: 'hello'} on
 * connect, then {type: 'jobs', jobs: [...]} with the whole list, right away and whenever a job changes.
 *
 * It reconnects when the connection drops, waiting twice as long after each attempt, up to MAX_RETRY_MS.
 */
const FIRST_RETRY_MS = 1000;
const MAX_RETRY_MS = 30 * 1000;

export class JobSocket {
    /**
     * @param getToken Resolves to the login token to connect with. The server finds the session by it, so it must be one
     *                 the server has seen: call the API with it first.
     * @param onJobs Called with the user's jobs, whenever they change
     * @param onNotifications Called when the organization's notifications changed, and on each (re)connect, since changes
     *                        may have been missed meanwhile (OSA-105)
     * @param onReconnect Called when the socket opens again after it dropped, e.g. because the API restarted and lost its
     *                    sessions (OSA-115). Not on the first open after connect().
     */
    constructor({getToken, onJobs, onNotifications = () => {}, onReconnect = () => {}}) {
        this._getToken = getToken;
        this._onJobs = onJobs;
        this._onNotifications = onNotifications;
        this._onReconnect = onReconnect;
        this._socket = null;
        this._retryMs = FIRST_RETRY_MS;
        this._retryTimer = null;
        this._closed = true;
        this._opened = false;
    }

    /**
     * Connect, or connect again, e.g. after the active user changed: the server binds the socket to the session's user
     */
    connect() {
        this.close();
        this._closed = false;
        this._opened = false;
        this._open();
    }

    close() {
        this._closed = true;
        clearTimeout(this._retryTimer);
        if (this._socket) {
            // A deliberate close doesn't reconnect
            this._socket.onclose = null;
            this._socket.close();
            this._socket = null;
        }
    }

    async _open() {
        let token;
        try {
            token = await this._getToken();
        } catch (e) {
            console.warn(e);
            this._retry();
            return;
        }
        if (this._closed) {
            return;
        }
        const location = window.location;
        const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
        // host, not hostname: it keeps the port, e.g. the dev server's
        const socket = new WebSocket(`${protocol}//${location.host}/api/ws?id_token=${token}`);
        this._socket = socket;

        socket.onopen = () => {
            this._retryMs = FIRST_RETRY_MS;
            if (this._opened) {
                this._onReconnect();
            }
            this._opened = true;
            this._onNotifications();
        };
        socket.onmessage = message => {
            let data;
            try {
                data = JSON.parse(message.data);
            } catch (e) {
                console.warn('Unexpected WebSocket message', message.data);
                return;
            }
            if (data && data.type === 'jobs' && Array.isArray(data.jobs)) {
                this._onJobs(data.jobs);
            } else if (data && data.type === 'notifications') {
                this._onNotifications();
            } else if (Array.isArray(data)) {
                // The list without a type, from a server older than OSA-102
                this._onJobs(data);
            }
        };
        socket.onclose = () => {
            this._socket = null;
            this._retry();
        };
    }

    _retry() {
        if (this._closed) {
            return;
        }
        clearTimeout(this._retryTimer);
        this._retryTimer = setTimeout(() => this._open(), this._retryMs);
        this._retryMs = Math.min(this._retryMs * 2, MAX_RETRY_MS);
    }
}
